import React, { useEffect, useRef } from "react"
import { Tooltip } from "components/Core"
import { HiOutlineFilter, HiX, HiOutlineSearch } from "react-icons/hi"
import cn from "classnames"

interface ColumnFilterProps {
  columnKey: string
  isOpen: boolean
  onToggle: (key: string | null) => void
  searchValue: any
  setSearchValue: (val: any) => void
  onApply: (params: any) => void
  onRemove: (id: string) => void
  estadoOmnixSearch?: any[]
  setEstadoOmnixSearch?: (val: any[]) => void
  isAvailableSearch?: boolean | null
  setIsAvailableSearch?: (val: boolean | null) => void
  onForceSearch?: () => void
}

const ColumnFilter: React.FC<ColumnFilterProps> = ({
  columnKey,
  isOpen,
  onToggle,
  searchValue,
  setSearchValue,
  onApply,
  onRemove,
  estadoOmnixSearch = [],
  setEstadoOmnixSearch,
  isAvailableSearch = null,
  setIsAvailableSearch,
  onForceSearch,
}) => {
  const filterRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement
      if (target.closest(".filter-trigger")) return
      if (filterRef.current && !filterRef.current.contains(event.target as Node)) {
        onToggle(null)
      }
    }
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside)
    }
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [isOpen, onToggle])

  const isFilterActive = columnKey === "Estado Omnix" 
    ? estadoOmnixSearch.length > 0 
    : columnKey === "Disponible"
      ? isAvailableSearch !== null
      : Boolean(searchValue)

  return (
    <div className="flex flex-row items-center gap-1.5 animate-slide-down-fader relative z-20">
      <Tooltip content={isOpen ? "Cerrar filtros" : `Filtrar por ${columnKey}`} align="top">
        <button
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            onToggle(isOpen ? null : columnKey)
          }}
          className={cn(
            "p-1.5 rounded-md transition-all duration-300 shadow-sm border filter-trigger",
            isFilterActive
              ? "bg-emerald-50 text-emerald-600 border-emerald-100 shadow-emerald-100/50"
              : isOpen
                ? "bg-blue-50 text-blue-600 border-blue-100"
                : "bg-white text-blue-500 border-transparent hover:bg-blue-50 hover:border-blue-100"
          )}
        >
          <HiOutlineFilter className="text-sm pointer-events-none" />
        </button>
      </Tooltip>

      {isFilterActive && (
        <Tooltip content="Limpiar filtro" align="top">
          <button
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              onRemove(columnKey === "Sku" ? "sku" : columnKey === "Serie SAP" ? "serieSap" : columnKey === "Disponible" ? "isAvailable" : "estadoOmnix")
              onToggle(null)
            }}
            className="p-1.5 bg-rose-50 text-rose-500 border border-rose-100 rounded-md hover:bg-rose-100 transition-colors shadow-sm flex items-center justify-center"
          >
            <HiX className="text-sm" />
          </button>
        </Tooltip>
      )}

      {isOpen && (
        <div 
          ref={filterRef}
          className={cn(
            "absolute z-50 animate-slide-down-fader shadow-2xl",
            columnKey === "Estado Omnix" || columnKey === "Disponible"
              ? "top-12 right-0 w-56 bg-white border border-gray-100 rounded-xl p-2 shadow-indigo-100/40" 
              : "top-12 right-0 w-48"
          )}
        >
          {columnKey === "Estado Omnix" ? (
            <div className="flex flex-col gap-1">
              {[
                { id: 0, label: "Libre" },
                { id: 1, label: "Vendido" },
                { id: 4, label: "Reservado" },
                { id: 6, label: "Eliminado" },
                { id: 7, label: "Bloqueado" },
              ].map((opt) => (
                <button
                  key={opt.id}
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    if (!setEstadoOmnixSearch) return
                    const isSelected = estadoOmnixSearch.includes(opt.id)
                    const next = isSelected 
                      ? estadoOmnixSearch.filter(id => id !== opt.id)
                      : [...estadoOmnixSearch, opt.id]
                    
                    setEstadoOmnixSearch(next)
                    if (next.length === 0) {
                      onRemove("estadoOmnix")
                    } else {
                      const optionsMap: Record<number, string> = {
                        0: "0 - libre", 1: "1 - vendido", 4: "4 - reservado", 6: "6 - eliminado", 7: "7 - bloqueado"
                      }
                      onApply({ 
                        id: "estadoOmnix", 
                        field: "estadoOmnix", 
                        label: "Estado Omnix", 
                        type: "string", 
                        value: next.map(id => optionsMap[id as number])
                      })
                    }
                  }}
                  className={cn(
                    "flex items-center justify-between px-3 py-2 rounded-lg text-xs font-medium transition-all text-left",
                    estadoOmnixSearch.includes(opt.id)
                      ? "bg-indigo-50 text-indigo-700"
                      : "hover:bg-gray-50 text-gray-500"
                  )}
                >
                  <span>{opt.id} - {opt.label}</span>
                  {estadoOmnixSearch.includes(opt.id) && (
                    <div className="w-1.5 h-1.5 rounded-full bg-indigo-500" />
                  )}
                </button>
              ))}
            </div>
          ) : columnKey === "Disponible" ? (
            <div className="flex flex-col gap-1">
              {[
                { id: true, label: "Si (Disponible)" },
                { id: false, label: "No (No disponible)" },
              ].map((opt) => (
                <button
                  key={String(opt.id)}
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    if (!setIsAvailableSearch) return
                    setIsAvailableSearch(opt.id)
                    onApply({ 
                      id: "isAvailable", 
                      field: "isAvailable", 
                      label: "Disponible", 
                      type: "boolean", 
                      value: opt.id 
                    })
                    onToggle(null)
                  }}
                  className={cn(
                    "flex items-center justify-between px-3 py-2 rounded-lg text-xs font-medium transition-all text-left",
                    isAvailableSearch === opt.id
                      ? "bg-emerald-50 text-emerald-700"
                      : "hover:bg-gray-50 text-gray-500"
                  )}
                >
                  <span>{opt.label}</span>
                  {isAvailableSearch === opt.id && (
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  )}
                </button>
              ))}
            </div>
          ) : (
            <div className="relative group/filter">
              <input
                type="text"
                placeholder={columnKey === "Sku" ? "SKUs (S1, S2...)" : "Series (A, B...)"}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault()
                    const filterId = columnKey === "Sku" ? "sku" : "serieSap"
                    if (!searchValue.trim()) {
                      onRemove(filterId)
                      onForceSearch?.()
                    } else {
                      onApply({ id: filterId, field: filterId, label: columnKey, type: "string", value: searchValue })
                    }
                  }
                }}
                className="w-full bg-white border border-gray-200 rounded-lg pl-3 pr-8 py-2 text-[11px] font-medium text-gray-600 outline-none focus:border-indigo-300 focus:ring-2 focus:ring-indigo-50 transition-all shadow-lg"
              />
              <button
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  const filterId = columnKey === "Sku" ? "sku" : "serieSap"
                  if (!searchValue.trim()) {
                    onRemove(filterId)
                    onForceSearch?.()
                  } else {
                    onApply({ id: filterId, field: filterId, label: columnKey, type: "string", value: searchValue })
                  }
                }}
                className="absolute right-1 top-1/2 -translate-y-1/2 p-2 text-gray-400 hover:text-indigo-500 rounded-md transition-colors"
              >
                <HiOutlineSearch className="text-sm" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default ColumnFilter
